import * as React from 'react';
import type { ValidationPropType, ValidHtmlElements } from './use-validation';
import { useValidation } from './use-validation';

export type TextFieldPropType = ValidationPropType;

export function useGenericField<TElement extends ValidHtmlElements>(
  inputRef: React.RefObject<TElement | null>,
  { error, validate }: ValidationPropType = {},
) {
  const [invalid, validationMessage, updateValidation, triggerInvalid] = useValidation(inputRef, error, validate);

  const onBlur: React.FocusEventHandler<TElement> = React.useCallback(
    e => {
      updateValidation(e.currentTarget);
    },
    [updateValidation],
  );

  const onChange: React.ChangeEventHandler<TElement> = React.useCallback(
    e => {
      if (invalid) updateValidation(e.currentTarget);
    },
    [invalid, updateValidation],
  );

  const onInvalid: React.FormEventHandler<TElement> = React.useCallback(
    e => {
      e.preventDefault();
      triggerInvalid(e.currentTarget);
    },
    [triggerInvalid],
  );

  return React.useMemo(
    () => ({ invalid, validationMessage, inputProps: { onBlur, onChange, onInvalid } } as const),
    [invalid, onBlur, onChange, onInvalid, validationMessage],
  );
}

export function useTextField(
  inputRef: React.RefObject<HTMLInputElement | null>,
  { error, validate }: ValidationPropType = {},
) {
  return useGenericField(inputRef, { error, validate });
}

export function useTextArea(
  inputRef: React.RefObject<HTMLTextAreaElement | null>,
  { error, validate }: ValidationPropType = {},
) {
  return useGenericField(inputRef, { error, validate });
}
